/**
 * SkipReasonDialog — small popover/dialog that lets the user pick a reason
 * (or just skip without one) when skipping a recurring instance.
 */

import { memo, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { skipRecurrenceInstance } from "@/db/operations"
import { cn } from "@/shared/utils/general/cn"

interface SkipReasonDialogProps {
  instanceId: string
  onClose: () => void
}

const REASONS = [
  { key: "busy", emoji: "🏃", labelKey: "recurrence.skipReason.busy", fallback: "Too busy" },
  { key: "sick", emoji: "🤒", labelKey: "recurrence.skipReason.sick", fallback: "Sick" },
  { key: "vacation", emoji: "🏖️", labelKey: "recurrence.skipReason.vacation", fallback: "Vacation" },
  { key: "not_needed", emoji: "🚫", labelKey: "recurrence.skipReason.notNeeded", fallback: "Not needed" },
  { key: "weather", emoji: "🌧️", labelKey: "recurrence.skipReason.weather", fallback: "Weather" },
] as const

export const SkipReasonDialog = memo(function SkipReasonDialog({ instanceId, onClose }: SkipReasonDialogProps) {
  const { t } = useTranslation()
  const [custom, setCustom] = useState("")

  const handleSkip = useCallback((reason?: string) => {
    void skipRecurrenceInstance(instanceId, reason)
    onClose()
  }, [instanceId, onClose])

  const handleCustomSubmit = useCallback(() => {
    handleSkip(custom.trim() || undefined)
  }, [handleSkip, custom])

  return (
    <div className="flex w-64 flex-col gap-1 p-2">
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        {t("recurrence.skipReason.title", "Why skip?")}
      </p>

      {REASONS.map((r) => (
        <button
          key={r.key}
          onClick={() => handleSkip(r.key)}
          className={cn(
            "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
            "hover:bg-accent transition-colors",
          )}
        >
          <span>{r.emoji}</span>
          <span>{t(r.labelKey, r.fallback)}</span>
        </button>
      ))}

      <div className="mt-1 flex items-center gap-1">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCustomSubmit()}
          placeholder={t("recurrence.skipReason.custom", "Other reason...")}
          className="flex-1 rounded-md border border-border bg-transparent px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      <button
        onClick={() => handleSkip(undefined)}
        className="mt-1 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
      >
        {t("recurrence.skipReason.noReason", "Skip without reason")}
      </button>
    </div>
  )
})
